'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { useAppSelector } from '@/store/hooks';
import { Home, CalendarCheck, Target, MessageCircle, User } from 'lucide-react';
import { motion } from 'framer-motion';

interface Tab {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const TABS: Tab[] = [
  { href: '/', label: '홈', icon: Home },
  { href: '/checkin', label: '체크인', icon: CalendarCheck },
  { href: '/goals', label: '목표', icon: Target },
  { href: '/coach', label: '코치', icon: MessageCircle },
  { href: '/profile', label: '내 정보', icon: User },
];

export default function BottomTabBar() {
  const pathname = usePathname();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  if (!isAuthenticated) return null;

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white/80 backdrop-blur-md border-t border-gray-200/50 safe-area-bottom" aria-label="메인 내비게이션">
      <div className="flex items-center justify-around h-16">
        {TABS.map((tab) => {
          const isActive = tab.href === '/'
            ? pathname === '/'
            : pathname.startsWith(tab.href);
          const Icon = tab.icon;

          return (
            <Link
              key={tab.href}
              href={tab.href}
              aria-current={isActive ? 'page' : undefined}
              className="relative flex flex-col items-center justify-center flex-1 h-full"
            >
              <Icon
                className={`w-5 h-5 transition-colors duration-200 ${
                  isActive
                    ? 'text-[var(--persona-primary)]'
                    : 'text-gray-400'
                }`}
              />
              <span
                className={`text-[10px] mt-0.5 transition-colors duration-200 leading-tight ${
                  isActive
                    ? 'text-[var(--persona-primary)] font-semibold'
                    : 'text-gray-400'
                }`}
              >
                {tab.label}
              </span>
              {isActive && (
                <motion.div
                  layoutId="bottomTabIndicator"
                  className="absolute -top-px left-[15%] right-[15%] h-1 rounded-full"
                  style={{ backgroundColor: 'var(--persona-primary)' }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
